import { useState } from 'react';
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { Check as CheckIcon, Flag as FlagIcon, OutlinedFlag as OutlinedFlagIcon } from '@mui/icons-material';
import FileDropArea from '../../../Atoms/FileDropArea';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { importAdviseSupplierResponse, previewAdviseSupplierResponse } from '../../../../Api/Advise/FeatureInput';
import { isNotFoundRefusal } from '../../../../utils/refusalMessage';
import { FeatureInputImported, FeatureInputPreview, FeatureInputSupplier } from '../../../../types/advise';
import { importedSummary, previewSummary, supplierRefusal, unansweredLine } from './SupplierFeatureInput.calc';
import { FeatureStateLabel } from './FeatureStateLabel';
import { SubtitledDialogTitle } from './SubtitledDialogTitle';
import { ShortlistSupplierPicker } from './ShortlistSupplierPicker';

const PREVIEW_FAILED = "Couldn't read the file. Please try again.";
const IMPORT_FAILED = "Couldn't import the response. Please try again.";

interface ImportResponseDialogProps {
  engagementId: string;
  engagementName: string;
  suppliers: FeatureInputSupplier[];
  supplierId: string;
  onClose: () => void;
  // Called once the answers are saved, so the cards and the Feature Responses tables show them.
  onImported: () => void;
  // Called when the API says the supplier has left the shortlist, so the view reloads it.
  onShortlistChanged: () => void;
}

/** Reads a supplier's completed sheet, shows how each answer was read, and imports it once the Analyst confirms. */
export const ImportResponseDialog = ({
  engagementId,
  engagementName,
  suppliers,
  supplierId: initialSupplierId,
  onClose,
  onImported,
  onShortlistChanged
}: ImportResponseDialogProps) => {
  const [supplierId, setSupplierId] = useState(initialSupplierId);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<FeatureInputPreview | null>(null);
  // What the preview was read from, so Confirm Import sends that file for that supplier whatever is picked since.
  const [previewedFrom, setPreviewedFrom] = useState<{ supplier: FeatureInputSupplier; file: File } | null>(null);
  // The rows to import flagged: those the read couldn't settle, until the Analyst clears them, and any they flag.
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [imported, setImported] = useState<FeatureInputImported | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const supplier = suppliers.find((row) => row.id === supplierId);
  // Once a refused import has reloaded the shortlist without them, sending it again can't succeed.
  const previewedSupplierGone = !!previewedFrom && !suppliers.some((row) => row.id === previewedFrom.supplier.id);

  const chooseSupplier = (id: string) => {
    if (busy) return;
    setSupplierId(id);
    setRefusal(null);
  };

  const refuse = (error: Parameters<typeof supplierRefusal>[0], fallback: string) => {
    setRefusal(supplierRefusal(error, fallback));
    if (isNotFoundRefusal(error)) onShortlistChanged();
  };

  const chooseFile = (files: File[]) => {
    if (busy) return;
    setFile(files[0] ?? null);
    setRefusal(null);
  };

  const handlePreview = async () => {
    if (!file || !supplier) return;
    const from = { supplier, file };
    setBusy(true);
    setRefusal(null);
    const { data, error } = await previewAdviseSupplierResponse(engagementId, supplier.id, file);
    setBusy(false);
    if (error || !data) {
      refuse(error, PREVIEW_FAILED);
      return;
    }
    setFlagged(new Set(data.rows.filter((row) => row.flagged).map((row) => row.feature_key)));
    setPreviewedFrom(from);
    setPreview(data);
  };

  const handleImport = async () => {
    if (!previewedFrom) return;
    setBusy(true);
    setRefusal(null);
    const { data, error } = await importAdviseSupplierResponse(
      engagementId,
      previewedFrom.supplier.id,
      previewedFrom.file,
      [...flagged]
    );
    setBusy(false);
    if (error || !data) {
      refuse(error, IMPORT_FAILED);
      return;
    }
    setImported(data);
    onImported();
  };

  const toggleFlagged = (featureKey: string) =>
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(featureKey)) next.delete(featureKey);
      else next.add(featureKey);
      return next;
    });

  if (imported) {
    return (
      <Dialog
        open
        onClose={onClose}
        maxWidth="xs"
        fullWidth>
        <DialogContent className="flex flex-col items-center gap-3 py-8 text-center">
          <CheckIcon
            color="success"
            fontSize="large"
          />
          <TextBlock
            size="lg"
            weight="semibold">
            {previewedFrom?.supplier.supplier_name}&apos;s response has been imported
          </TextBlock>
          <TextBlock
            size="sm"
            color="grey6">
            {importedSummary(imported)}
          </TextBlock>
          <TextButton onClick={onClose}>View Feature Responses →</TextButton>
        </DialogContent>
      </Dialog>
    );
  }

  const title = (
    <SubtitledDialogTitle
      title={preview ? 'Import Response — Parsed Preview' : 'Import Response'}
      subtitle={
        preview
          ? `${preview.file_name} · parsed from ${previewedFrom?.supplier.supplier_name}'s completed file`
          : `${engagementName} · Feature Responses`
      }
      busy={busy}
      onClose={onClose}
    />
  );

  if (preview) {
    return (
      <Dialog
        open
        onClose={busy ? undefined : onClose}
        maxWidth="md"
        fullWidth>
        {title}
        <DialogContent className="flex flex-col gap-3">
          {refusal && <Alert severity="error">{refusal}</Alert>}
          <TextBlock
            size="sm"
            weight="semibold"
            color={preview.rows.some((row) => row.flagged) ? 'orange' : 'grey6'}>
            {previewSummary(preview)}
          </TextBlock>
          {unansweredLine(preview) && (
            <TextBlock
              size="sm"
              color="grey6">
              {unansweredLine(preview)}
            </TextBlock>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell>Vendor&apos;s answer (raw)</TableCell>
                <TableCell>Mapped state</TableCell>
                <TableCell padding="checkbox">Flag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.flatMap((row) => {
                const cells = (
                  <TableRow
                    key={row.feature_key}
                    sx={row.flagged ? { '& td': { borderBottom: 'none' } } : undefined}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      <TextSpan
                        size="sm"
                        color="grey6">
                        &ldquo;{row.answer}&rdquo;
                      </TextSpan>
                    </TableCell>
                    <TableCell>
                      <FeatureStateLabel state={row.state} />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        icon={<OutlinedFlagIcon color="disabled" />}
                        checkedIcon={<FlagIcon color="warning" />}
                        checked={flagged.has(row.feature_key)}
                        onChange={() => toggleFlagged(row.feature_key)}
                        inputProps={{ 'aria-label': `Flag ${row.label}` }}
                      />
                    </TableCell>
                  </TableRow>
                );
                if (!row.flagged) return [cells];
                return [
                  cells,
                  <TableRow key={`${row.feature_key}-reason`}>
                    <TableCell colSpan={4}>
                      <TextSpan
                        size="sm"
                        color="orange">
                        {row.reason}
                      </TextSpan>
                    </TableCell>
                  </TableRow>
                ];
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            onClick={() => {
              setPreview(null);
              setRefusal(null);
            }}
            disabled={busy}>
            Back
          </button>
          <button
            type="button"
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={handleImport}
            disabled={busy || previewedSupplierGone}>
            {busy ? (
              <ComponentSpinner sx={{ height: '19px' }} />
            ) : (
              `Confirm Import (${preview.rows.length} ${preview.rows.length === 1 ? 'feature' : 'features'})`
            )}
          </button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth>
      {title}
      <DialogContent className="flex flex-col gap-4">
        {refusal && <Alert severity="error">{refusal}</Alert>}
        <ShortlistSupplierPicker
          suppliers={suppliers}
          value={supplierId}
          onChange={chooseSupplier}
          disabled={busy}
        />
        <FileDropArea
          instructions="Drop the selected supplier's completed sheet here, or browse"
          subInstructions=".xlsx only — should be the same sheet exported to them earlier, with their answers filled in"
          buttonText="Browse Files"
          allowedFileTypes={['.xlsx']}
          onFileDrop={chooseFile}
        />
        {file && (
          <TextBlock
            size="sm"
            color="grey6">
            Selected: {file.name}
          </TextBlock>
        )}
        <Alert severity="warning">
          This should be the exact file exported for the selected supplier — a file from another supplier will be
          rejected.
        </Alert>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onClose}
          disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={handlePreview}
          disabled={!file || !supplier || busy}>
          {busy ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Parse & Preview →'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
